import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, User, Phone, Check, X, Shield, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { getCompanyId, getToken } from "@/utils/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    role: "member",
    password: "",
    permissions: []
  });

  const companyId = getCompanyId();
  
  console.log("Company ID:", companyId);

  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchModules();
  }, [companyId]);

  const fetchUsers = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/companies/${companyId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/modules`);
      setModules(response.data);
    } catch (error) {
      console.error("Failed to load modules");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = getToken();
      if (editingUser) {
        // Update user
        const payload = { ...formData };
        if (!payload.password) delete payload.password; // Don't send empty password
        
        await axios.patch(`${BACKEND_URL}/api/companies/${companyId}/users/${editingUser.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("User updated successfully");
      } else {
        // Create user
        await axios.post(`${BACKEND_URL}/api/companies/${companyId}/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("User created successfully");
      }
      fetchUsers();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingUser ? 'update' : 'create'} user`);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user?")) return;
    
    try {
      const token = getToken();
      await axios.delete(`${BACKEND_URL}/api/companies/${companyId}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User removed successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to remove user");
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      full_name: user.full_name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      password: "", // Leave empty to keep existing password
      permissions: user.permissions || []
    });
    setIsDialogOpen(true);
  };

  const togglePermission = (moduleCode) => {
    setFormData(prev => {
      const permissions = prev.permissions.includes(moduleCode)
        ? prev.permissions.filter(p => p !== moduleCode)
        : [...prev.permissions, moduleCode];
      return { ...prev, permissions };
    });
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      role: "member",
      password: "",
      permissions: []
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            User Management
          </h1>
          <p className="text-zinc-500">Manage users and their permissions</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold">
              <Plus size={20} className="mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl" aria-describedby="user-dialog-description">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
            </DialogHeader>
            <p id="user-dialog-description" className="sr-only">
              {editingUser ? 'Edit existing user details and permissions' : 'Create a new user with role and permissions'}
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    placeholder="John Doe"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={!!editingUser}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    placeholder="+1 234 567 890"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger id="role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="password">{editingUser ? 'Password (leave blank to keep)' : 'Password *'}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                  />
                </div>
              </div>

              {formData.role === 'admin' && (
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Shield size={16} />
                    Permissions
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {modules.map((module) => (
                      <div
                        key={module.code}
                        className={`
                          flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all
                          ${formData.permissions.includes(module.code)
                            ? 'bg-lime-50 border-lime-200'
                            : 'bg-zinc-50 border-zinc-200 hover:border-zinc-300'}
                        `}
                        onClick={() => togglePermission(module.code)}
                      >
                        <span className="text-sm font-medium">{module.name}</span>
                        {formData.permissions.includes(module.code) ? (
                          <Check size={16} className="text-lime-600" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border border-zinc-300" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold">
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto shadow-sm">
        <table className="w-full whitespace-nowrap">
          <thead className="bg-zinc-50 text-zinc-500 font-medium text-xs uppercase tracking-wider">
            <tr>
              <th className="px-6 py-4 text-left">User</th>
              <th className="px-6 py-4 text-left">Contact</th>
              <th className="px-6 py-4 text-left">Role</th>
              <th className="px-6 py-4 text-left">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-zinc-500">
                  No users found. Add your first user to get started.
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr
                  key={user.id}
                  className="hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-lime-100 rounded-full flex items-center justify-center">
                        <User size={20} className="text-lime-600" />
                      </div>
                      <div>
                        <div className="font-bold text-zinc-900">{user.full_name}</div>
                        <div className="text-xs text-zinc-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-600">
                      <Phone size={14} />
                      {user.phone}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge className={`${
                      user.role === 'admin'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-zinc-100 text-zinc-800'
                    }`}>
                      {user.role}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Badge className={`${
                      user.status === 'active'
                        ? 'bg-lime-100 text-lime-800'
                        : 'bg-zinc-100 text-zinc-800'
                    }`}>
                      {user.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => handleEdit(user)}>
                        <Edit2 size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDelete(user.id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;